import React, { useState } from 'react';
import UnicornScene from 'unicornstudio-react';
import { signInWithPopup, signInWithRedirect, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth, googleProvider } from '../../firebase';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [forgotError, setForgotError] = useState('');
  const { t, i18n } = useTranslation();
  const pageLanguage = i18n.language?.startsWith('nl') ? 'nl' : 'en';
  const loginVideoSrc = pageLanguage === 'nl'
    ? '/videos/Homepage/Mission_Pitch_superkonnected-login-nl.mp4'
    : '/videos/Homepage/Mission_Pitch_superkonnected-login-eng.mp4';

  const handlePageLanguageChange = (languageCode: 'nl' | 'en') => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('i18nextLng', languageCode);
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      sessionStorage.setItem('skGoogleLoginStarted', '1');
      await signInWithPopup(auth, googleProvider);
      setIsGoogleLoading(false);
    } catch (error: any) {
      console.error('Google login error:', error);

      // Fallback to redirect when popup is blocked/closed by browser or extension policy.
      if (
        error?.code === 'auth/popup-blocked' ||
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request'
      ) {
        try {
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch (redirectError: any) {
          const detail = redirectError?.code || redirectError?.message || 'onbekende fout';
          setError(`${t('loginPage.auth.errors.googleFailed')} (${detail})`);
          setIsGoogleLoading(false);
          return;
        }
      }

      if (error?.code === 'auth/unauthorized-domain') {
        setError('Deze domeinnaam staat nog niet toegestaan in Firebase Authentication.');
      } else {
        const detail = error?.code || error?.message || 'onbekende fout';
        setError(`${t('loginPage.auth.errors.googleFailed')} (${detail})`);
      }
      setIsGoogleLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state change will be handled by App.tsx
    } catch (error: any) {
      console.error('Email login error:', error);
      
      let errorMessage = t('loginPage.auth.errors.loginFailed');
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        errorMessage = t('loginPage.auth.errors.invalidCredentials');
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t('loginPage.auth.errors.invalidEmail');
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t('loginPage.auth.errors.tooManyRequests');
      }
      
      setError(errorMessage);
    }
  };

  const handleForgotPassword = () => {
    setForgotEmail(email);
    setForgotStatus('idle');
    setForgotError('');
    setShowForgotModal(true);
  };

  const handleForgotSubmit = async () => {
    if (!forgotEmail.trim()) return;
    setForgotStatus('sending');
    setForgotError('');
    try {
      await sendPasswordResetEmail(auth, forgotEmail.trim());
      setForgotStatus('sent');
    } catch (err: any) {
      setForgotStatus('error');
      if (err.code === 'auth/invalid-email') {
        setForgotError(t('loginPage.auth.errors.invalidEmail'));
      } else if (err.code === 'auth/user-not-found') {
        setForgotError(t('loginPage.auth.errors.userNotFound'));
      } else {
        setForgotError(t('loginPage.auth.errors.generic'));
      }
    }
  };

  return (
    <div style={styles.container}>
      {/* Login sectie wrapper */}
      <div style={{ position: 'relative', height: '100vh' }}>
      {/* Unicorn Studio Background */}
      <div style={styles.unicornWrapper}>
        <UnicornScene
          projectId="1pxbIbvnnWbZoL0UKobx"
          sdkUrl="https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v2.0.5/dist/unicornStudio.umd.js"
          width="100%"
          height="100%"
        />
      </div>

      <div style={marketingStyles.videoLanguageToggle} aria-label="Taal kiezen">
        <button
          type="button"
          onClick={() => handlePageLanguageChange('nl')}
          style={{
            ...marketingStyles.videoLanguageButton,
            ...(pageLanguage === 'nl' ? marketingStyles.videoLanguageButtonActive : {}),
          }}
        >
          NL
        </button>
        <button
          type="button"
          onClick={() => handlePageLanguageChange('en')}
          style={{
            ...marketingStyles.videoLanguageButton,
            ...(pageLanguage === 'en' ? marketingStyles.videoLanguageButtonActive : {}),
          }}
        >
          ENG
        </button>
      </div>

      {/* Brand Labels */}
      <div style={styles.brandName}>SuperKonnected</div>
      <div style={styles.brandSubTagline}>{t('loginPage.hero.subTagline')}</div>
      <div style={styles.brandTagline}>{t('loginPage.hero.tagline')}</div>

      {/* Login Card */}
      <div id="login" style={styles.cardContainer}>
        <div style={styles.loginCard}>
          {/* Logo */}
          <img 
            src="/SuperKonnected_Logo.svg" 
            alt="SuperKonnected" 
            style={styles.logo}
          />

          {/* Title */}
          <h1 style={styles.title}>{t('loginPage.auth.title')}</h1>
          <p style={styles.subtitle}>{t('loginPage.auth.subtitle')}</p>

          {!showEmailLogin ? (
            <>
              {error && (
                <div style={styles.error}>{error}</div>
              )}

              {/* Google Login Button */}
              <button
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                style={styles.googleButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.351z" fill="#4285F4"/>
                  <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853"/>
                  <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.341-2.59z" fill="#FBBC05"/>
                  <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.341 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335"/>
                </svg>
                {isGoogleLoading ? 'Google openen...' : t('loginPage.auth.continueWithGoogle')}
              </button>

              {/* Divider */}
              <DividerWithLines label={t('loginPage.auth.or')} />

              {/* Email Login Toggle */}
              <button
                onClick={() => setShowEmailLogin(true)}
                style={styles.emailToggle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.85)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('loginPage.auth.emailPassword')}
              </button>
            </>
          ) : (
            <>
              {/* Email/Password Form */}
              <form onSubmit={handleEmailLogin}>
                {error && (
                  <div style={styles.error}>{error}</div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('loginPage.auth.emailLabel')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('loginPage.auth.emailPlaceholder')}
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('loginPage.auth.passwordLabel')}</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('loginPage.auth.passwordPlaceholder')}
                    required
                    style={styles.input}
                  />
                </div>

                <div style={styles.forgotPassword}>
                  <a href="#" onClick={(e) => { e.preventDefault(); handleForgotPassword(); }} style={styles.forgotLink}>
                    {t('loginPage.auth.forgotPassword')}
                  </a>
                </div>

                <button type="submit" style={styles.submitButton}>
                  {t('loginPage.auth.loginButton')}
                </button>

                <button
                  type="button"
                  onClick={() => { setShowEmailLogin(false); setError(''); }}
                  style={{...styles.emailToggle, marginTop: '12px'}}
                >
                  {t('common.back')}
                </button>
              </form>
            </>
          )}

          {/* Legal Footer */}
          <div style={styles.legalFooter}>
            <p style={styles.legalText}>
              {t('loginPage.auth.legalPrefix')}{' '}
              <a href="#terms" style={styles.legalLink}>
                {t('loginPage.auth.terms')}
              </a>
              {' '}{t('loginPage.auth.legalAnd')}{' '}
              <a href="#privacy" style={styles.legalLink}>
                {t('loginPage.auth.privacy')}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Scroll-down pijl */}
      <div style={marketingStyles.scrollArrow}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5v14M5 12l7 7 7-7" stroke="#8BA59B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      </div>{/* einde login sectie wrapper */}

      {/* Animation keyframes injected via style tag */}
      <style>{`
        @keyframes fadeInDelayed {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scrollPulse {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(6px); opacity: 1; }
        }
      `}</style>

      {/* ===== MARKETING SECTIE ===== */}
      <div style={marketingStyles.wrapper}>

        {/* Sectie 1 - Value Statement */}
        <section style={marketingStyles.section}>
          <div style={{...marketingStyles.sectionInner, ...marketingStyles.introSectionInner}}>
            <div style={marketingStyles.sectionTextColumn}>
              <h2 style={marketingStyles.h2}>{t('loginPage.marketing.intro.title')}</h2>
              <p style={marketingStyles.body}>
                {t('loginPage.marketing.intro.body')}
              </p>
              <a href="#login" style={marketingStyles.cta}>{t('loginPage.marketing.cta')}</a>
            </div>
            <div style={marketingStyles.introVideoCard}>
              <video
                key={loginVideoSrc}
                controls
                playsInline
                preload="metadata"
                style={marketingStyles.introVideo}
              >
                <source src={loginVideoSrc} type="video/mp4" />
                {t('loginPage.marketing.videoUnsupported')}
              </video>
            </div>
          </div>
        </section>

        {/* Sectie 2 - Het Probleem */}
        <section style={{...marketingStyles.section, background: 'rgba(0,0,0,0.02)'}}>
          <div style={marketingStyles.sectionInner}>
            <div style={marketingStyles.sectionTextColumn}>
              <h2 style={marketingStyles.h2}>
                {t('loginPage.marketing.problem.titleLine1')}<br />{t('loginPage.marketing.problem.titleLine2')}
              </h2>
              <ul style={marketingStyles.list}>
                <li>{t('loginPage.marketing.problem.item1')}</li>
                <li>{t('loginPage.marketing.problem.item2')}</li>
                <li>{t('loginPage.marketing.problem.item3')}</li>
              </ul>
              <p style={marketingStyles.closing}>
                {t('loginPage.marketing.problem.closing')}
              </p>
            </div>
          </div>
        </section>

        {/* Sectie 3 - Features */}
        <section style={marketingStyles.section}>
          <div style={marketingStyles.featureSectionInner}>
          <h2 style={marketingStyles.h2}>{t('loginPage.marketing.features.title')}</h2>
          <div style={marketingStyles.featureTopRow}>
            <div style={{...marketingStyles.featureCard, ...marketingStyles.featureCardPrimary}}>
              <h3 style={marketingStyles.featureTitleLarge}>{t('loginPage.marketing.features.connectionTitle')}</h3>
              <p style={marketingStyles.featureBodyLarge}>
                {t('loginPage.marketing.features.connectionBody1')}
              </p>
              <p style={marketingStyles.featureBodyLarge}>
                {t('loginPage.marketing.features.connectionBody2')}
              </p>
              <p style={marketingStyles.featureBodyLarge}>
                {t('loginPage.marketing.features.connectionBody3')}
              </p>
            </div>
            <div style={{...marketingStyles.featureCard, ...marketingStyles.featureCardPrimary}}>
              <h3 style={marketingStyles.featureTitleLarge}>{t('loginPage.marketing.features.geoTitle')}</h3>
              <p style={marketingStyles.featureBodyLarge}>
                {t('loginPage.marketing.features.geoBody1')}
              </p>
              <p style={marketingStyles.featureBodyLarge}>
                {t('loginPage.marketing.features.geoBody2')}
              </p>
              <p style={marketingStyles.featureBodyLarge}>
                {t('loginPage.marketing.features.geoBody3')}
              </p>
            </div>
          </div>
          <div style={marketingStyles.featureBottomRow}>
            <div style={{...marketingStyles.featureCard, ...marketingStyles.featureCardSecondary}}>
              <h3 style={marketingStyles.featureTitle}>{t('loginPage.marketing.features.cleanmindTitle')}</h3>
              <p style={marketingStyles.featureBody}>
                {t('loginPage.marketing.features.cleanmindBody1')}
              </p>
              <p style={marketingStyles.featureBody}>
                {t('loginPage.marketing.features.cleanmindBody2')}
              </p>
              <ul style={marketingStyles.featureList}>
                <li>{t('loginPage.marketing.features.cleanmindItem1')}</li>
                <li>{t('loginPage.marketing.features.cleanmindItem2')}</li>
                <li>{t('loginPage.marketing.features.cleanmindItem3')}</li>
              </ul>
              <p style={marketingStyles.featureBody}>
                {t('loginPage.marketing.features.cleanmindBody3')}
              </p>
            </div>
            <div style={{...marketingStyles.featureCard, ...marketingStyles.featureCardSecondary}}>
              <h3 style={marketingStyles.featureTitle}>{t('loginPage.marketing.features.focusTitle')}</h3>
              <p style={marketingStyles.featureBody}>
                {t('loginPage.marketing.features.focusBody1')}
              </p>
              <p style={marketingStyles.featureBody}>
                {t('loginPage.marketing.features.focusBody2')}
              </p>
            </div>
          </div>
          <div style={marketingStyles.featureCtaWrap}>
            <a href="#login" style={marketingStyles.cta}>{t('loginPage.marketing.cta')}</a>
          </div>
          </div>
        </section>

        {/* Sectie 4 - Product Flow */}
        <section style={{...marketingStyles.section, background: 'rgba(0,0,0,0.02)'}}>
          <div style={marketingStyles.sectionInner}>
            <div style={marketingStyles.productFlowHeader}>
              <h2 style={marketingStyles.h2}>{t('loginPage.marketing.productFlow.title')}</h2>
              <p style={marketingStyles.body}>{t('loginPage.marketing.productFlow.subtitle')}</p>
            </div>
            <div style={marketingStyles.productFlowTrack}>
              <div style={marketingStyles.productFlowCard}>
                <div style={marketingStyles.productMockupFeed}>
                  <div style={marketingStyles.productMockupPostHeader}>
                    <div style={marketingStyles.productAvatar}>
                      <span style={marketingStyles.productAvatarFallback}>AM</span>
                      <img
                        src="/assets/avatars/amina.jpg"
                        alt=""
                        style={marketingStyles.productAvatarImage}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                    <div>
                      <div style={marketingStyles.productMockupName}>Amina</div>
                      <div style={marketingStyles.productMockupMeta}>{t('loginPage.marketing.productFlow.feedMeta')}</div>
                    </div>
                  </div>
                  <div style={marketingStyles.productMockupLine} />
                  <div style={{...marketingStyles.productMockupLine, width: '74%'}} />
                  <div style={marketingStyles.focusControl}>
                    <span>{t('loginPage.marketing.productFlow.focusControl')}</span>
                    <span style={marketingStyles.focusToggle}><span style={marketingStyles.focusToggleDot} /></span>
                  </div>
                </div>
                <h3 style={marketingStyles.productFlowTitle}>{t('loginPage.marketing.productFlow.feedTitle')}</h3>
                <p style={marketingStyles.productFlowText}>{t('loginPage.marketing.productFlow.feedBody')}</p>
              </div>

              <div style={marketingStyles.productFlowConnector} />

              <div style={marketingStyles.productFlowCard}>
                <div style={marketingStyles.productRespectVisual}>
                  <div style={marketingStyles.productAvatarLarge}>
                    <span style={marketingStyles.productAvatarFallback}>JV</span>
                    <img
                      src="/assets/avatars/jeroen.jpg"
                      alt=""
                      style={marketingStyles.productAvatarImage}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                  <div style={marketingStyles.respectBadge}>{t('loginPage.marketing.productFlow.respectBadge')}</div>
                </div>
                <h3 style={marketingStyles.productFlowTitle}>{t('loginPage.marketing.productFlow.respectTitle')}</h3>
                <p style={marketingStyles.productFlowText}>{t('loginPage.marketing.productFlow.respectBody')}</p>
              </div>

              <div style={marketingStyles.productFlowConnector} />

              <div style={marketingStyles.productFlowCard}>
                <div style={marketingStyles.mutualRespectVisual}>
                  <div style={marketingStyles.productAvatarLarge}>
                    <span style={marketingStyles.productAvatarFallback}>AM</span>
                    <img
                      src="/assets/avatars/amina.jpg"
                      alt=""
                      style={marketingStyles.productAvatarImage}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                  <div style={marketingStyles.mutualLine} />
                  <div style={marketingStyles.productAvatarLarge}>
                    <span style={marketingStyles.productAvatarFallback}>JV</span>
                    <img
                      src="/assets/avatars/jeroen.jpg"
                      alt=""
                      style={marketingStyles.productAvatarImage}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                </div>
                <div style={marketingStyles.mutualBadge}>{t('loginPage.marketing.productFlow.mutualBadge')}</div>
                <h3 style={marketingStyles.productFlowTitle}>{t('loginPage.marketing.productFlow.mutualTitle')}</h3>
                <p style={marketingStyles.productFlowText}>{t('loginPage.marketing.productFlow.mutualBody')}</p>
              </div>

              <div style={marketingStyles.productFlowConnector} />

              <div style={marketingStyles.productFlowCard}>
                <div style={marketingStyles.connectionFlowMini}>
                  <span style={marketingStyles.productFlowChip}>{t('loginPage.marketing.productFlow.chat')}</span>
                  <span style={marketingStyles.productFlowChip}>{t('loginPage.marketing.productFlow.call')}</span>
                  <span style={marketingStyles.productFlowChip}>{t('loginPage.marketing.productFlow.video')}</span>
                  <span style={marketingStyles.productFlowChip}>{t('loginPage.marketing.productFlow.meeting')}</span>
                </div>
                <h3 style={marketingStyles.productFlowTitle}>{t('loginPage.marketing.productFlow.connectionTitle')}</h3>
                <p style={marketingStyles.productFlowText}>{t('loginPage.marketing.productFlow.connectionBody')}</p>
              </div>

              <div style={marketingStyles.productFlowConnector} />

              <div style={marketingStyles.productFlowCard}>
                <div style={marketingStyles.agendaMockup}>
                  <div style={marketingStyles.agendaMockupDate}>{t('loginPage.marketing.productFlow.agendaDate')}</div>
                  <div style={marketingStyles.agendaMockupTitle}>{t('loginPage.marketing.productFlow.agendaEvent')}</div>
                  <div style={marketingStyles.aiNote}>{t('loginPage.marketing.productFlow.aiNote')}</div>
                </div>
                <h3 style={marketingStyles.productFlowTitle}>{t('loginPage.marketing.productFlow.agendaTitle')}</h3>
                <p style={marketingStyles.productFlowText}>{t('loginPage.marketing.productFlow.agendaBody')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Sectie 5 - Zo werkt het */}
        <section style={{...marketingStyles.section, background: 'rgba(0,0,0,0.02)'}}>
          <div style={marketingStyles.sectionInner}>
            <div style={marketingStyles.sectionTextColumn}>
              <h2 style={marketingStyles.h2}>{t('loginPage.marketing.how.title')}</h2>
              <div style={marketingStyles.steps}>
                <div style={marketingStyles.step}>
                  <span style={marketingStyles.stepNum}>1</span>
                  {t('loginPage.marketing.how.step1')}
                </div>
                <div style={marketingStyles.step}>
                  <span style={marketingStyles.stepNum}>2</span>
                  {t('loginPage.marketing.how.step2')}
                </div>
                <div style={marketingStyles.step}>
                  <span style={marketingStyles.stepNum}>3</span>
                  {t('loginPage.marketing.how.step3')}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sectie 6 - Big Vision */}
        <section style={marketingStyles.compactSection}>
          <div style={marketingStyles.sectionInner}>
            <div style={marketingStyles.sectionTextColumn}>
              <h2 style={marketingStyles.h2}>{t('loginPage.marketing.bigVision.title')}</h2>
              <p style={marketingStyles.body}>
                {t('loginPage.marketing.bigVision.body')}
              </p>
            </div>
          </div>
        </section>

        {/* Sectie 7 - Missie */}
        <section style={marketingStyles.tightSection}>
          <div style={marketingStyles.sectionInner}>
            <div style={marketingStyles.sectionTextColumn}>
              <h2 style={marketingStyles.h2}>{t('loginPage.marketing.mission.title')}</h2>
              <p style={marketingStyles.body}>
                {t('loginPage.marketing.mission.body1')}
              </p>
              <p style={marketingStyles.body}>
                {t('loginPage.marketing.mission.body2')}
              </p>
              <a href="#login" style={marketingStyles.cta}>{t('loginPage.marketing.cta')}</a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div style={marketingStyles.footer}>
          <p style={marketingStyles.footerText}>{t('loginPage.marketing.footer')}</p>
        </div>

      </div>

      {/* Wachtwoord vergeten modal */}
      {showForgotModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }} onClick={() => setShowForgotModal(false)}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '400px', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#1C1C1C' }}>
              Wachtwoord vergeten?
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#5A5A5A', lineHeight: 1.5 }}>
              Vul je e-mailadres in. We sturen je een link om je wachtwoord opnieuw in te stellen.
            </p>

            {forgotStatus !== 'sent' ? (
              <>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleForgotSubmit()}
                  placeholder="jouw@emailadres.nl"
                  autoFocus
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '12px 14px', fontSize: '14px',
                    border: '1px solid #E0DDD8', borderRadius: '10px',
                    outline: 'none', marginBottom: '12px',
                    fontFamily: 'inherit',
                  }}
                />
                {forgotError && (
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#E05252' }}>{forgotError}</p>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowForgotModal(false)}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #E0DDD8',
                      background: '#fff', color: '#1C1C1C', fontSize: '14px', fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >Annuleren</button>
                  <button
                    onClick={handleForgotSubmit}
                    disabled={forgotStatus === 'sending'}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
                      background: '#2E5F56', color: '#fff', fontSize: '14px', fontWeight: 600,
                      cursor: forgotStatus === 'sending' ? 'not-allowed' : 'pointer',
                      opacity: forgotStatus === 'sending' ? 0.7 : 1, fontFamily: 'inherit',
                    }}
                  >{forgotStatus === 'sending' ? 'Versturen...' : 'Verstuur link'}</button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: '14px', color: '#2E5F56', fontWeight: 600, margin: '0 0 20px' }}>
                  ✓ E-mail verstuurd naar {forgotEmail}
                </p>
                <p style={{ fontSize: '13px', color: '#5A5A5A', margin: '0 0 20px', lineHeight: 1.5 }}>
                  Controleer ook je spamfolder als je de mail niet ziet.
                </p>
                <button
                  onClick={() => setShowForgotModal(false)}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
                    background: '#2E5F56', color: '#fff', fontSize: '14px', fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >Sluiten</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    minHeight: '100vh',
    overflowY: 'auto',
    position: 'relative',
    background: 'linear-gradient(135deg, #E8E6E1 0%, #D8D6D1 100%)',
  },
  unicornWrapper: {
    position: 'fixed',
    top: '20px',
    left: '20px',
    width: 'calc(100vw - 40px)',
    height: 'calc(100vh - 40px)',
    zIndex: 0,
    borderRadius: '32px',
    overflow: 'hidden',
    border: 'none',
    background: 'radial-gradient(circle at top left, rgba(139, 165, 155, 0.35), transparent 38%), radial-gradient(circle at bottom right, rgba(122, 148, 136, 0.28), transparent 32%), linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(244,241,236,0.92) 52%, rgba(232,230,225,0.98) 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), 0 24px 80px rgba(80, 72, 64, 0.08)',
  },
  brandName: {
    position: 'fixed',
    top: '40px',
    left: '40px',
    zIndex: 10,
    fontSize: '18px',
    fontWeight: 600,
    color: '#8BA59B',
    letterSpacing: '-0.2px',
  },
  brandSubTagline: {
    position: 'fixed',
    bottom: '62px',
    left: '40px',
    zIndex: 10,
    fontSize: '12px',
    fontWeight: 400,
    fontStyle: 'italic',
    color: '#8BA59B',
    letterSpacing: '0.1px',
    opacity: 0.7,
  },
  brandTagline: {
    position: 'fixed',
    bottom: '40px',
    left: '40px',
    zIndex: 10,
    fontSize: '14px',
    fontWeight: 400,
    color: '#8BA59B',
    letterSpacing: '0.1px',
    opacity: 0.85,
  },
  cardContainer: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 calc(33.333% - 185px) 0 0',
  },
  loginCard: {
    background: 'rgba(248, 246, 243, 0.95)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    borderRadius: '24px',
    padding: '32px 28px',
    width: '100%',
    maxWidth: '370px',
    boxShadow: '0 40px 80px rgba(0, 0, 0, 0.08)',
    animation: 'fadeInDelayed 500ms ease-out 300ms both',
  },
  logo: {
    width: '48px',
    height: 'auto',
    margin: '0 auto 16px',
    display: 'block',
    opacity: 0.75,
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.06))',
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: 'rgba(60, 55, 50, 0.92)',
    marginBottom: '6px',
    letterSpacing: '-0.3px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    color: 'rgba(80, 75, 70, 0.65)',
    marginBottom: '28px',
    fontWeight: 400,
    textAlign: 'center',
  },
  googleButton: {
    width: '100%',
    padding: '11px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(50, 45, 40, 0.85)',
    background: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    letterSpacing: '0.2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    margin: '20px 0',
  },
  dividerText: {
    padding: '0 14px',
    color: 'rgba(100, 95, 90, 0.6)',
    fontSize: '12px',
    fontWeight: 400,
    position: 'relative' as 'relative',
    background: 'rgba(248, 246, 243, 0.95)',
    zIndex: 1,
  },
  emailToggle: {
    width: '100%',
    padding: '11px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(50, 45, 40, 0.85)',
    background: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    letterSpacing: '0.2px',
  },
  formGroup: {
    marginBottom: '18px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: 'rgba(70, 65, 60, 0.75)',
    marginBottom: '7px',
    letterSpacing: '0.1px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    fontSize: '14px',
    color: 'rgba(50, 45, 40, 0.92)',
    background: 'rgba(255, 255, 255, 0.7)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 200ms ease',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as 'border-box',
  },
  submitButton: {
    width: '100%',
    padding: '11px',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(50, 45, 40, 0.85)',
    background: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 200ms ease',
    marginTop: '6px',
    letterSpacing: '0.2px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  },
  forgotPassword: {
    textAlign: 'right' as 'right',
    marginTop: '6px',
  },
  forgotLink: {
    color: 'rgba(100, 95, 90, 0.7)',
    fontSize: '12px',
    fontWeight: 400,
    textDecoration: 'none',
    transition: 'color 200ms ease',
  },
  error: {
    background: 'rgba(255, 100, 100, 0.1)',
    border: '1px solid rgba(255, 100, 100, 0.3)',
    borderRadius: '8px',
    padding: '10px',
    marginBottom: '16px',
    fontSize: '13px',
    color: 'rgba(200, 50, 50, 0.9)',
  },
  legalFooter: {
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
    textAlign: 'center' as 'center',
  },
  legalText: {
    fontSize: '11px',
    color: 'rgba(100, 95, 90, 0.7)',
    lineHeight: '1.5',
  },
  legalLink: {
    color: '#8BA59B',
    textDecoration: 'underline',
    transition: 'color 200ms ease',
  },
};

const marketingStyles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    position: 'relative',
    zIndex: 1,
    background: '#F8F6F3',
  },
  section: {
    width: '100%',
    padding: '80px 32px',
  },
  compactSection: {
    width: '100%',
    padding: '72px 32px 48px',
  },
  tightSection: {
    width: '100%',
    padding: '24px 32px 48px',
  },
  sectionInner: {
    maxWidth: '1120px',
    margin: '0 auto',
  },
  introSectionInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '56px',
    flexWrap: 'wrap',
  },
  sectionTextColumn: {
    maxWidth: '760px',
    flex: '1 1 420px',
  },
  introVideoCard: {
    position: 'relative',
    flex: '1 1 420px',
    maxWidth: '540px',
  },
  videoLanguageToggle: {
    position: 'fixed',
    top: '40px',
    right: '40px',
    zIndex: 15,
    display: 'flex',
    width: 'fit-content',
    gap: '3px',
    padding: '4px',
    borderRadius: '999px',
    background: 'rgba(248, 246, 243, 0.86)',
    border: '1px solid rgba(139, 165, 155, 0.22)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    opacity: 0.78,
  },
  videoLanguageButton: {
    border: 'none',
    borderRadius: '999px',
    padding: '5px 10px',
    background: 'transparent',
    color: 'rgba(40, 35, 30, 0.64)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  videoLanguageButtonActive: {
    background: 'rgba(139, 165, 155, 0.16)',
    color: 'rgba(40, 35, 30, 0.86)',
  },
  introVideo: {
    display: 'block',
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'cover',
    borderRadius: '20px',
    background: '#E8E6E1',
  },
  featureSectionInner: {
    maxWidth: '1120px',
    margin: '0 auto',
  },
  h2: {
    fontSize: '34px',
    fontWeight: 700,
    color: 'rgba(40, 35, 30, 0.92)',
    letterSpacing: '-0.7px',
    lineHeight: 1.2,
    marginBottom: '22px',
  },
  body: {
    fontSize: '18px',
    color: 'rgba(70, 65, 60, 0.75)',
    lineHeight: 1.8,
    marginBottom: '34px',
  },
  closing: {
    fontSize: '17px',
    fontWeight: 500,
    color: '#8BA59B',
    marginTop: '24px',
    lineHeight: 1.7,
  },
  cta: {
    display: 'inline-block',
    padding: '14px 32px',
    background: '#8BA59B',
    color: '#fff',
    borderRadius: '12px',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '0.1px',
  },
  list: {
    paddingLeft: '20px',
    fontSize: '17px',
    color: 'rgba(70, 65, 60, 0.75)',
    lineHeight: 1.95,
    marginBottom: '20px',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '24px',
    marginBottom: '40px',
  },
  featureTopRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    gap: '28px',
    marginBottom: '28px',
    alignItems: 'stretch',
  },
  featureBottomRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '24px',
    marginBottom: '40px',
    alignItems: 'stretch',
  },
  featureCard: {
    background: 'rgba(255,255,255,0.82)',
    borderRadius: '28px',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 18px 50px rgba(33, 28, 24, 0.06)',
    backdropFilter: 'blur(8px)',
  },
  featureCardPrimary: {
    padding: '36px',
    minHeight: '320px',
  },
  featureCardSecondary: {
    padding: '32px',
    minHeight: '260px',
  },
  featureTitle: {
    fontSize: '22px',
    fontWeight: 600,
    color: 'rgba(40, 35, 30, 0.9)',
    letterSpacing: '-0.3px',
    lineHeight: 1.25,
    margin: '0 0 16px 0',
  },
  featureTitleLarge: {
    fontSize: '30px',
    fontWeight: 650,
    color: 'rgba(40, 35, 30, 0.94)',
    letterSpacing: '-0.7px',
    lineHeight: 1.15,
    margin: '0 0 18px 0',
  },
  featureBody: {
    fontSize: '16px',
    color: 'rgba(70, 65, 60, 0.76)',
    lineHeight: 1.75,
    margin: '0 0 14px 0',
  },
  featureBodyLarge: {
    fontSize: '18px',
    color: 'rgba(70, 65, 60, 0.78)',
    lineHeight: 1.8,
    letterSpacing: '-0.1px',
    margin: '0 0 16px 0',
  },
  featureList: {
    paddingLeft: '22px',
    fontSize: '15px',
    color: 'rgba(70, 65, 60, 0.72)',
    lineHeight: 1.9,
    margin: '0 0 16px 0',
  },
  featureCtaWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '8px',
  },
  productFlowHeader: {
    maxWidth: '760px',
    marginBottom: '40px',
  },
  productFlowTrack: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '14px',
    flexWrap: 'wrap',
  },
  productFlowCard: {
    flex: '1 1 180px',
    minWidth: '180px',
    background: 'rgba(255,255,255,0.78)',
    border: '1px solid rgba(139, 165, 155, 0.18)',
    borderRadius: '24px',
    padding: '20px',
    boxShadow: '0 16px 44px rgba(33, 28, 24, 0.05)',
    minHeight: '270px',
  },
  productFlowConnector: {
    alignSelf: 'center',
    width: '36px',
    borderTop: '2px dashed rgba(139, 165, 155, 0.32)',
  },
  productFlowTitle: {
    fontSize: '17px',
    fontWeight: 650,
    color: 'rgba(40, 35, 30, 0.9)',
    letterSpacing: '-0.2px',
    margin: '18px 0 8px',
    lineHeight: 1.25,
  },
  productFlowText: {
    fontSize: '13px',
    lineHeight: 1.65,
    color: 'rgba(70, 65, 60, 0.72)',
    margin: 0,
  },
  productMockupFeed: {
    borderRadius: '18px',
    padding: '14px',
    background: 'linear-gradient(135deg, rgba(248,246,243,0.98), rgba(238,235,229,0.8))',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  productMockupPostHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  productAvatar: {
    position: 'relative',
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: '#8BA59B',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    overflow: 'hidden',
  },
  productAvatarLarge: {
    position: 'relative',
    width: '46px',
    height: '46px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #8BA59B, #6F8B80)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    boxShadow: '0 10px 24px rgba(111, 139, 128, 0.18)',
    overflow: 'hidden',
  },
  productAvatarImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  productAvatarFallback: {
    position: 'relative',
    zIndex: 0,
  },
  productMockupName: {
    fontSize: '12px',
    fontWeight: 650,
    color: 'rgba(40,35,30,0.82)',
  },
  productMockupMeta: {
    fontSize: '10px',
    color: 'rgba(70,65,60,0.5)',
  },
  productMockupLine: {
    height: '7px',
    borderRadius: '999px',
    background: 'rgba(139, 165, 155, 0.16)',
    marginBottom: '7px',
  },
  focusControl: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '12px',
    paddingTop: '10px',
    borderTop: '1px dashed rgba(139, 165, 155, 0.24)',
    fontSize: '10px',
    fontWeight: 650,
    color: 'rgba(70,65,60,0.68)',
  },
  focusToggle: {
    width: '34px',
    height: '18px',
    borderRadius: '999px',
    background: 'rgba(139, 165, 155, 0.26)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '2px',
  },
  focusToggleDot: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    background: '#8BA59B',
  },
  productRespectVisual: {
    minHeight: '96px',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    borderRadius: '18px',
    background: 'rgba(139, 165, 155, 0.08)',
    border: '1px dashed rgba(139, 165, 155, 0.25)',
  },
  respectBadge: {
    padding: '6px 10px',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.82)',
    color: '#6F8B80',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.2px',
  },
  mutualRespectVisual: {
    minHeight: '74px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mutualLine: {
    width: '50px',
    borderTop: '2px dashed rgba(139, 165, 155, 0.4)',
  },
  mutualBadge: {
    textAlign: 'center' as 'center',
    margin: '8px auto 0',
    width: 'fit-content',
    padding: '6px 10px',
    borderRadius: '999px',
    background: 'rgba(139, 165, 155, 0.12)',
    color: '#6F8B80',
    fontSize: '10px',
    fontWeight: 700,
  },
  connectionFlowMini: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px',
    padding: '12px',
    borderRadius: '18px',
    background: 'rgba(248,246,243,0.86)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  productFlowChip: {
    padding: '7px 9px',
    borderRadius: '999px',
    background: 'rgba(139, 165, 155, 0.1)',
    color: 'rgba(70,65,60,0.72)',
    fontSize: '11px',
    fontWeight: 650,
  },
  agendaMockup: {
    borderRadius: '18px',
    padding: '14px',
    background: 'rgba(248,246,243,0.9)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  agendaMockupDate: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#8BA59B',
    letterSpacing: '0.5px',
    marginBottom: '7px',
  },
  agendaMockupTitle: {
    fontSize: '13px',
    fontWeight: 650,
    color: 'rgba(40,35,30,0.84)',
    marginBottom: '10px',
  },
  aiNote: {
    padding: '8px',
    borderRadius: '12px',
    background: 'rgba(139, 165, 155, 0.1)',
    color: 'rgba(70,65,60,0.74)',
    fontSize: '10px',
    lineHeight: 1.45,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    gap: '22px',
    marginTop: '18px',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '17px',
    color: 'rgba(60, 55, 50, 0.85)',
    lineHeight: 1.6,
  },
  stepNum: {
    width: '36px',
    height: '36px',
    minWidth: '36px',
    borderRadius: '50%',
    background: '#8BA59B',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '15px',
  },
  scrollArrow: {
    position: 'absolute' as 'absolute',
    bottom: '40px',
    right: '40px',
    zIndex: 20,
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(139, 165, 155, 0.12)',
    border: '1px solid rgba(139, 165, 155, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'scrollPulse 2s ease-in-out infinite',
    cursor: 'default',
  },
  footer: {
    borderTop: '1px solid rgba(0,0,0,0.06)',
    padding: '32px',
    textAlign: 'center' as 'center',
  },
  footerText: {
    fontSize: '12px',
    color: 'rgba(100, 95, 90, 0.5)',
  },
};

// Add divider lines via pseudo-elements using a wrapper
const DividerWithLines: React.FC<{ label: string }> = ({ label }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
  }}>
    <div style={{flex: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.06)'}} />
    <span style={styles.dividerText}>{label}</span>
    <div style={{flex: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.06)'}} />
  </div>
);

export default LoginPage;
